import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { User2, History, Info, Pencil } from 'lucide-react'
import Header from './header'
import { AutoComplete } from '@/components/ui/autocomplete'
import type { Option } from '@/components/ui/autocomplete'
import { toast } from '@/components/ui/use-toast'
import { pharmacyClient } from '@/connect'
import { CreatePurchaseInvoiceRequest } from '@/gen/purchase_invoice/create_purchase_invoice_pb'
import { PaymentMethod, PaymentType } from '@/gen/purchase_invoice/common_pb'
import { InventoryItem, ItemType, Quantity, ItemMetadata, DiscountType } from '@/gen/inventory/common_pb'
import { Supplier } from '@/gen/suppliers/common_pb'
import { Route as createPurchaseInvoiceRoute } from '@/routes/(inventory)/branch/_layout/purchase-invoices/create/index'
import { useRouter } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { format } from 'date-fns'
import { CreatePurchaseInvoiceComponent } from '@/components/purchase-invoice/create-branch'
import { InvoiceDetailsDialog } from '@/components/purchase-invoice/invoice-details-dialog'
import { StockPointType } from '@/gen/auth/common_pb'

type Row = {
  product: Option | undefined
  productInput: string
  batchNo: string
  expiryDate?: Date
  productionDate?: Date
  cost: string
  price: string
  quantity: string
  bonus: string
  customBarcode: string
  discountType: string
  discountValue: string
  note: string
  type?: 'MEDICAL' | 'NON_MEDICAL'
  masterId?: string
}

const Index = () => {
  const data = createPurchaseInvoiceRoute.useLoaderData()
  const router = useRouter()
  
  const [selectedSupplier, setSelectedSupplier] = useState<Option | undefined>()
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<Option | undefined>()
  const [supplierInput, setSupplierInput] = useState("")
  const [poInput, setPoInput] = useState("")
  
  const [invoiceNumber, setInvoiceNumber] = useState("")
  const [invoiceDate, setInvoiceDate] = useState<Date | undefined>(new Date())
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(new Date())
  const [paymentMethod, setPaymentMethod] = useState<string>("CASH")
  const [paymentType, setPaymentType] = useState<string>("INSTANT")
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false)
  const [selectedStockPoint, setSelectedStockPoint] = useState<Option | undefined>()
  const [stockPointInput, setStockPointInput] = useState("")

  
  const [rows, setRows] = useState<Row[]>([
    { 
      product: undefined, 
      productInput: '', 
      batchNo: '', 
      cost: '', 
      price: '', 
      quantity: '', 
      bonus: '', 
      customBarcode: '',
      discountType: DiscountType.NONE.toString(),
      discountValue: '0',
      note: ''
    }
  ])
  const [isLoading, setIsLoading] = useState(false)

  const handleProductChange = (index: number, value: Option) => {
    console.log('=== PRODUCT CHANGE DEBUG ===');
    console.log('Full Option object:', value);
    console.log('value.value:', value.value);
    console.log('value.type:', value.type);
    console.log('value.medical_master_id:', value.medical_master_id);
    console.log('value.non_medical_master_id:', value.non_medical_master_id);
    console.log('All keys:', Object.keys(value));
    setRows(prev => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        product: value,
        productInput: value.label,
        type: value.type as 'MEDICAL' | 'NON_MEDICAL',
        masterId: value.value,
      }
      
      // Fetch price for medical items
      if (value.type === 'MEDICAL' && value.value) {
        pharmacyClient.getPriceHistory({
          id: value.value,
          page: 1,
          pageSize: 1
        }).then(response => {
          const priceHistory = response.priceHistories || []
          const currentPrice = priceHistory.find(p => p.isCurrentActivePriceForPharmacy)
          if (currentPrice) {
            setRows(prev => {
              const updated = [...prev]
              updated[index] = {
                ...updated[index],
                price: currentPrice.price.toString()
              }
              return updated
            })
          }
        }).catch(err => {
          console.error('Error fetching price:', err)
        })
      }
      
      // Add new row if selecting product in last row
      if (index === prev.length - 1) {
        updated.push({ 
          product: undefined, 
          productInput: '', 
          batchNo: '', 
          cost: '', 
          price: '', 
          quantity: '', 
          bonus: '', 
          customBarcode: '',
          discountType: DiscountType.NONE.toString(),
          discountValue: '0',
          note: ''
        })
      }
      return updated
    })
  }

  const handleRowChange = (index: number, field: string, value: any) => {
    setRows(prev => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        [field]: value
      }
      return updated
    })
  }

  const handleClearRow = (index: number) => {
    setRows(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (!selectedSupplier) {
      toast({
        title: "Error",
        description: "Please select a supplier",
        variant: "destructive",
        duration: 3000,
      })
      return
    }

    if (!invoiceNumber) {
      toast({
        title: "Error",
        description: "Please enter invoice number",
        variant: "destructive",
        duration: 3000,
      })
      return
    }

    const rowsToValidate = rows.slice(0, -1)
    if (rowsToValidate.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one item",
        variant: "destructive",
        duration: 3000,
      })
      return
    }

    for (const row of rowsToValidate) {
      if (!row.product || !row.batchNo || !row.quantity || !row.cost) {
        toast({
          title: "Error",
          description: "Please fill in all required fields (Product, Batch, Quantity, Cost) for each row",
          variant: "destructive",
          duration: 3000,
        })
        return
      }
    }

    try {
      setIsLoading(true)
      const request = new CreatePurchaseInvoiceRequest()

      console.log('=== BEFORE SETTING ===');
      console.log('selectedSupplier:', selectedSupplier);
      console.log('selectedSupplier.value:', selectedSupplier?.value);

      request.supplierId = selectedSupplier.value
      request.invoiceNumber = invoiceNumber

      request.invoiceDate = invoiceDate ? dayjs(invoiceDate).unix() : dayjs().unix()
      request.deliveryDate = deliveryDate ? dayjs(deliveryDate).unix() : dayjs().unix()
      
      if (paymentMethod === "CASH") request.paymentMethod = PaymentMethod.CASH
      else if (paymentMethod === "CHEQUE") request.paymentMethod = PaymentMethod.CHEQUE
      else if (paymentMethod === "BANK_TRANSFER") request.paymentMethod = PaymentMethod.BANK_TRANSFER
      
      if (paymentType === "INSTANT") request.paymentType = PaymentType.INSTANT
      else if (paymentType === "CREDIT") request.paymentType = PaymentType.CREDIT
      
      if (selectedPurchaseOrder) {
        request.purchaseOrderId = selectedPurchaseOrder.value
      }

      if (selectedStockPoint) {
        request.stockPointId = selectedStockPoint.value
      }

      const items: InventoryItem[] = []
      for (const row of rowsToValidate) {
        console.log('=== SUBMIT ROW DEBUG ===');
        console.log('Row:', row);
        console.log('row.product:', row.product);
        console.log('row.masterId:', row.masterId);
        console.log('row.type:', row.type);
        const item = new InventoryItem()
        
        if (row.type === 'MEDICAL') {
          item.itemType = ItemType.MEDICAL
          item.masterId = row.product!.value
        } else {
          item.itemType = ItemType.NON_MEDICAL
          item.masterId = row.product!.value
        }

        console.log('Final masterId being sent:', item.masterId);
        
        item.batchNumber = row.batchNo
        item.expiryDate = row.expiryDate ? BigInt(dayjs(row.expiryDate).unix()) : BigInt(dayjs().add(1, 'year').unix())
        item.productionDate = row.productionDate ? BigInt(dayjs(row.productionDate).unix()) : BigInt(0)
        item.costFromSupplier = parseFloat(row.cost) || 0
        item.sellingPrice = parseFloat(row.price) || 0
        
        const quantity = new Quantity()
        quantity.original = parseInt(row.quantity) || 0
        quantity.bonus = parseInt(row.bonus) || 0
        item.quantity = quantity
        
        const metadata = new ItemMetadata()
        metadata.customBarcode = row.customBarcode || ""
        metadata.discountType = parseInt(row.discountType) || DiscountType.NONE
        metadata.discountValue = parseFloat(row.discountValue) || 0
        metadata.isDiscountable = metadata.discountType !== DiscountType.NONE
        item.metadata = metadata
        
        item.isValidExpiryDate = row.expiryDate ? dayjs(row.expiryDate).isAfter(dayjs()) : true
        item.isValidProductionDate = row.productionDate ? dayjs(row.productionDate).isBefore(dayjs()) : true
        
        items.push(item)
      }
      request.items = items

      console.log('=== FINAL REQUEST OBJECT ===');
      console.log('Full request:', request);
      console.log('request.supplierId:', request.supplierId);
      console.log('request.purchaseOrderId:', request.purchaseOrderId);
      console.log('request.items:', request.items);
      console.log('Request as JSON:', JSON.stringify(request, (key, value) =>
        typeof value === 'bigint' ? value.toString() : value
      , 2));

      const response = await pharmacyClient.createPurchaseInvoice(request)
      
      setIsLoading(false)
      toast({
        title: "Success",
        description: `Purchase invoice ${response.purchaseInvoiceNumber} created successfully`,
        variant: "success",
        duration: 3000,
      })
      
      router.navigate({ to: '/branch/purchase-invoices' })
    } catch (error) {
      console.error("Error creating purchase invoice:", error)
      toast({
        title: "Error",
        description: "Failed to create purchase invoice. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
      setIsLoading(false)
    }
  }

  return (
    <div className="h-full w-full max-w-screen border rounded-xl flex flex-col overflow-hidden">
      <Header />
      <hr className='mt-2' />
      
      <div className="flex items-center justify-between px-2 py-2">
        <div className="flex items-center gap-2 min-w-[320px]">
          <div className="w-[280px]">
            <AutoComplete
              options={data.suppliers.map((supplier: Supplier) => ({
                value: supplier.id,
                label: supplier.name,
              }))}
              value={selectedSupplier}
              onValueChange={setSelectedSupplier}
              input={supplierInput}
              onInputChanged={setSupplierInput}
              emptyMessage="No suppliers found."
              placeholder="Select Supplier"
              isLoading={false}
              disabled={false}
            />
          </div>

          <div className="w-[280px]">
          <AutoComplete
            options={data.stockPoints?.stockPoints
              ?.filter(s => s.stockPointType === StockPointType.RECEIVING)
              .map((stockPoint) => ({
                value: stockPoint.id,
                label: stockPoint.name,
              })) || []}
            value={selectedStockPoint}
            onValueChange={setSelectedStockPoint}
            input={stockPointInput}
            onInputChanged={setStockPointInput}
            emptyMessage="No stock points found."
            placeholder="Select Stock Point"
            isLoading={false}
            disabled={false}
          />
        </div>

          <div className="w-[280px]">
            <AutoComplete
              options={data.purchaseOrders.map((po) => ({
                value: po.id,
                label: po.orderNumber || `PO-${po.id.slice(0, 8)}`,
              }))}
              value={selectedPurchaseOrder}
              onValueChange={setSelectedPurchaseOrder}
              input={poInput}
              onInputChanged={setPoInput}
              emptyMessage="No purchase orders found."
              placeholder="Select Purchase Order"
              isLoading={false}
              disabled={false}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline">
            <User2 className="w-3 h-3 mr-1" />Change Supplier
          </Button>
          <Button size="sm" variant="outline">
            <History className="w-3 h-3 mr-1" />View Past Invoices
          </Button>
          <Button size="sm" variant="outline">
            <Info className="w-3 h-3 mr-1" />View Supplier Info
          </Button>
        </div>
      </div>
      
      <hr />
      
      {/* Invoice Summary Bar */}
      <div className="px-2 py-2 bg-gray-50 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap text-sm">
            <span>
              <span className="text-gray-600 font-medium">Invoice #:</span> {invoiceNumber || <span className="text-gray-400">Not set</span>}
            </span>
            <span>
              <span className="text-gray-600 font-medium">Invoice Date:</span> {invoiceDate ? format(invoiceDate, 'dd/MM/yyyy') : <span className="text-gray-400">Not set</span>}
            </span>
            <span>
              <span className="text-gray-600 font-medium">Delivery Date:</span> {deliveryDate ? format(deliveryDate, 'dd/MM/yyyy') : <span className="text-gray-400">Not set</span>}
            </span>
            <span>
              <span className="text-gray-600 font-medium">Payment:</span> {paymentMethod ? paymentMethod.replace('_', ' ') : <span className="text-gray-400">Not set</span>} / {paymentType || <span className="text-gray-400">Not set</span>}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowInvoiceDialog(true)}
            className="text-xs"
          >
            <Pencil className="w-3 h-3 mr-1" />
            Edit Invoice Details
          </Button>
        </div>
      </div>
      
      <hr />
      
      <CreatePurchaseInvoiceComponent
        rows={rows}
        handleProductChange={handleProductChange}
        handleRowChange={handleRowChange}
        handleClearRow={handleClearRow}
        handleSubmit={handleSubmit}
        loading={isLoading}
      />

      <InvoiceDetailsDialog
        open={showInvoiceDialog}
        onOpenChange={setShowInvoiceDialog}
        invoiceNumber={invoiceNumber}
        setInvoiceNumber={setInvoiceNumber}
        invoiceDate={invoiceDate}
        setInvoiceDate={setInvoiceDate}
        deliveryDate={deliveryDate}
        setDeliveryDate={setDeliveryDate}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        paymentType={paymentType}
        setPaymentType={setPaymentType}
      />
    </div>
  )
}

export default Index